import type { PrintJob, PrintLine, PrintRaster } from "./print.js";

const MAX_HEIGHT = 4096;
const LOGO_CACHE = "templeos-approved-print-logo-v1";
const FONT = '"Nirmala UI", "Noto Sans Gujarati", "Noto Sans Devanagari", sans-serif';
export const needsRaster = (job: PrintJob, logo = ""): boolean => Boolean(logo || job.lines.some((line) => /[^\x20-\x7e]/.test(line.text)));

/** Most-significant bit first, one means black: ESC/POS GS v 0 row-major bitmap. */
export function monochrome(rgba: Uint8ClampedArray, width: 384 | 576, height: number): PrintRaster {
  if ((width !== 384 && width !== 576) || !Number.isInteger(height) || height < 1 || height > MAX_HEIGHT || rgba.length !== width * height * 4) throw new Error("Receipt bitmap dimensions are invalid.");
  const bytes = new Uint8Array((width / 8) * height);
  for (let p = 0; p < width * height; p++) {
    const offset = p * 4;
    const alpha = rgba[offset + 3]! / 255;
    const brightness = (0.299 * rgba[offset]! + 0.587 * rgba[offset + 1]! + 0.114 * rgba[offset + 2]!) * alpha + 255 * (1 - alpha);
    if (brightness < 180) bytes[p >> 3] = bytes[p >> 3]! | (0x80 >> (p & 7));
  }
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return { width, height, dataBase64: btoa(binary) };
}

/** Wrap complete grapheme clusters, allowing the browser to shape each resulting line. Nothing is clipped. */
export function wrapText(text: string, width: number, measure: (s: string) => number): string[] {
  const segmenter = new Intl.Segmenter(undefined, { granularity: "grapheme" });
  return text.split(/\r?\n/).flatMap((paragraph) => {
    const rows: string[] = [];
    let row = "";
    for (const { segment } of segmenter.segment(paragraph)) {
      if (measure(segment) > width) throw new Error("A receipt character is wider than the printer paper.");
      if (row && measure(row + segment) > width) { rows.push(row); row = ""; }
      row += segment;
    }
    rows.push(row);
    return rows;
  });
}

export async function logoImage(value: string): Promise<HTMLImageElement> {
  const url = new URL(value, location.origin);
  if (url.origin !== location.origin || url.username || url.password) throw new Error("Receipt logo must be an approved same-origin image.");
  const cache = "caches" in globalThis ? await caches.open(LOGO_CACHE) : null;
  let response: Response;
  try {
    response = await fetch(url.href, { signal: AbortSignal.timeout(3000), cache: "no-cache" });
    if (!response.ok) throw new Error("Logo request failed.");
  } catch {
    const stored = await cache?.match(url.href);
    if (!stored) throw new Error("The approved receipt logo is unavailable. Load it once online before using this counter offline.");
    response = stored;
  }
  const blob = await response.blob();
  if (!blob.type.startsWith("image/") || blob.size > 1024 * 1024) throw new Error("Receipt logo must be an image no larger than 1 MiB.");
  const image = new Image();
  let binary = "";
  for (const byte of new Uint8Array(await blob.arrayBuffer())) binary += String.fromCharCode(byte);
  image.src = `data:${blob.type};base64,${btoa(binary)}`;
  await image.decode();
  if (!image.naturalWidth || !image.naturalHeight || image.naturalWidth > 4096 || image.naturalHeight > 4096) throw new Error("Receipt logo dimensions must be at most 4096 pixels.");
  await cache?.put(url.href, new Response(blob, { headers: { "Content-Type": blob.type } }));
  return image;
}

/** Browser Canvas uses the browser's Indic shaper and installed fonts; no printer code page is assumed. */
export async function rasterize(job: PrintJob, paperMm: number, logo = ""): Promise<PrintJob> {
  if (paperMm !== 58 && paperMm !== 80) throw new Error("Set the agent paper width to 58 or 80 mm before raster printing.");
  if (!document.fonts) throw new Error("This browser cannot prepare receipt fonts.");
  await document.fonts.ready;
  const width = paperMm === 58 ? 384 : 576;
  const fontSize = paperMm === 58 ? 18 : 22;
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) throw new Error("This browser cannot render thermal receipt images.");
  const image = logo ? await logoImage(logo) : null;
  const imageWidth = image ? Math.min(width - 16, 160, image.naturalWidth) : 0;
  const imageHeight = image ? Math.round(imageWidth * image.naturalHeight / image.naturalWidth) : 0;
  if (imageHeight > 256) throw new Error("Receipt logo is too tall. Use a compact approved logo.");
  const rows: { text: string; style: PrintLine; font: string; height: number; pixels: number }[] = [];
  let height = 8 + (image ? imageHeight + 12 : 0);
  for (const line of job.lines) {
    const pixels = fontSize * (line.size ?? 1);
    const family = /[^\x20-\x7e]/.test(line.text) ? FONT : "monospace";
    const font = `${line.bold ? "bold" : "normal"} ${pixels}px ${family}`;
    await document.fonts.load(font, line.text || " ");
    context.font = font;
    for (const text of wrapText(line.text, width - 16, (s) => context.measureText(s).width)) {
      const rowHeight = Math.ceil(pixels * 1.5);
      height += rowHeight;
      if (height + 8 > MAX_HEIGHT) throw new Error("Receipt is too long for bitmap printing (4096 pixels). Split the slip or use browser printing.");
      rows.push({ text, style: line, font, height: rowHeight, pixels });
    }
  }
  canvas.width = width; canvas.height = height + 8;
  context.fillStyle = "white"; context.fillRect(0, 0, width, canvas.height);
  let y = 8;
  if (image) { context.drawImage(image, (width - imageWidth) / 2, y, imageWidth, imageHeight); y += imageHeight + 12; }
  context.fillStyle = "black"; context.textBaseline = "alphabetic";
  for (const row of rows) {
    context.font = row.font;
    context.textAlign = row.style.align ?? "left";
    const x = row.style.align === "center" ? width / 2 : row.style.align === "right" ? width - 8 : 8;
    context.fillText(row.text, x, y + row.pixels * 1.12);
    y += row.height;
  }
  return { ...job, raster: monochrome(context.getImageData(0, 0, width, canvas.height).data, width, canvas.height) };
}
